import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError, HrError } from '@kitobxonlik/contracts';

/**
 * HTTP qatlami. Prefiks /api/v1/literacy, xato JSON
 * { trace_id, error: { code, message, fields } }.
 *
 * AUTENTIFIKATSIYA: access va refresh tokenlar httpOnly cookie'da
 * (`withCredentials: true`), JS ularni ko'rmaydi. Access token muddati
 * tugaganda (401) interceptor BIR MARTA `POST /v1/auth/refresh` qiladi va
 * so'rovni qaytadan yuboradi — foydalanuvchi buni sezmaydi.
 *
 * Komponentlar axios'ni to'g'ridan chaqirmaydi — faqat api/*.api.ts orqali.
 */
export const API_BASE = `${import.meta.env.VITE_API_URL || '/api'}/v1/literacy`;

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;
  readonly traceId: string | null;
  /**
   * HR servisi xatosi: HR'ning statusi, o'z kodi va xabari. Xabarning o'zi
   * (`message`) ham shularni o'z ichiga oladi — notification'da tayyor matn.
   */
  readonly hr: HrError | null;

  constructor(
    status: number,
    code: string,
    message: string,
    fields: Record<string, string> = {},
    traceId: string | null = null,
    hr: HrError | null = null,
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.traceId = traceId;
    this.hr = hr;
  }
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  /*
   * Default Content-Type JSON bo'lgani uchun axios `post(url, null)` ni
   * JSON.stringify(null) = literal "null" tana bilan yuboradi — backend
   * 400 (`"null" is not valid JSON`) qaytaradi. Tanasiz so'rovlar (exam start,
   * member org add, oxirgi chunk'siz is_last) uchun null → undefined.
   */
  transformRequest: [
    (data: unknown) => (data === null ? undefined : data),
    ...(Array.isArray(axios.defaults.transformRequest)
      ? axios.defaults.transformRequest
      : [axios.defaults.transformRequest]),
  ].filter((fn): fn is NonNullable<typeof fn> => typeof fn === 'function'),
});

let unauthorizedHandler: (() => void) | null = null;

/** Refresh muvaffaqiyatli bo'lganda (sessiya yangilandi) — main.ts o'rnatadi */
let sessionRefreshedHandler: (() => void) | null = null;

/** 401 kelganda va refresh ham yordam bermaganda — main.ts o'rnatadi */
export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

export function setSessionRefreshedHandler(handler: () => void): void {
  sessionRefreshedHandler = handler;
}

/** Refresh oqimining O'ZI qayta urinmasligi kerak — cheksiz sikl bo'lardi */
const AUTH_PATHS = [
  '/v1/auth/refresh',
  '/v1/auth/logout',
  '/v1/auth/one-id',
  '/v1/auth/hr-entry',
  '/v1/auth/dev-login',
  '/v1/auth/dev-hr-link',
];

function isAuthPath(url: string | undefined): boolean {
  return !!url && AUTH_PATHS.some((path) => url.includes(path));
}

/*
 * SINGLE-FLIGHT — nega generatsiya hisoblagichi bilan.
 *
 * Refresh token ROTATSIYA qilinadi: har muvaffaqiyatli refresh eski tokenni
 * bekor qiladi. Backend bekor qilingan token qayta kelishini "o'g'irlangan"
 * deb hisoblab, foydalanuvchining BARCHA sessiyalarini yopadi.
 *
 * Ya'ni ikkita bir vaqtda ketgan refresh = foydalanuvchi tizimdan chiqib
 * ketishi. Faqat "hozir ketayotgan so'rovni kutish" yetarli emas: refresh
 * tugagan, lekin undan OLDIN yuborilgan boshqa so'rov 401 bilan qaytgan
 * bo'lishi mumkin — u yana refresh boshlardi.
 *
 * Shuning uchun har chiquvchi so'rov joriy generatsiya bilan belgilanadi.
 * 401 kelganda generatsiya oshgan bo'lsa — demak kimdir allaqachon
 * yangilagan, shunchaki QAYTA YUBORAMIZ.
 */
let refreshGeneration = 0;
let refreshing: Promise<boolean> | null = null;

type TrackedConfig = InternalAxiosRequestConfig & { _gen?: number; _retried?: boolean };

api.interceptors.request.use((config) => {
  (config as TrackedConfig)._gen = refreshGeneration;
  return config;
});

async function refreshSession(seenGeneration: number): Promise<boolean> {
  // Boshqa so'rov biz 401 olgunimizcha yangilab bo'lgan
  if (refreshGeneration > seenGeneration) return true;

  refreshing ??= (async () => {
    try {
      // `api` emas, toza axios: interceptor zanjiriga qaytib tushmasin
      await axios.post(`${API_BASE}/v1/auth/refresh`, null, { withCredentials: true });
      refreshGeneration += 1;
      sessionRefreshedHandler?.();
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const status = error.response?.status ?? 0;
    const body = error.response?.data;
    const config = error.config as TrackedConfig | undefined;

    if (status === 401 && config && !config._retried && !isAuthPath(config.url)) {
      config._retried = true;

      if (await refreshSession(config._gen ?? refreshGeneration)) {
        return api.request(config);
      }
    }

    if (status === 401) {
      unauthorizedHandler?.();
    }

    if (body && typeof body === 'object' && 'error' in body && body.error) {
      return Promise.reject(
        new ApiRequestError(
          status,
          body.error.code ?? '',
          body.error.message ?? 'Serverda xatolik',
          body.error.fields ?? {},
          body.trace_id ?? null,
          body.error.hr ?? null,
        ),
      );
    }

    return Promise.reject(
      new ApiRequestError(status, '', status === 0 ? 'Server bilan aloqa yöq' : error.message),
    );
  },
);

/** Faqat matn/parametrlarni tozalaydi: bo'sh satr va undefined yuborilmaydi */
export function cleanParams<T extends Record<string, unknown>>(params: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out as Partial<T>;
}

/**
 * Fayl preview/download URL'lari — `<img>`/`<video>` teglari uchun.
 * Cookie'ni brauzer o'zi yuboradi; `Authorization` header qo'yib bo'lmaganini
 * hisobga olsak, token cookie'da bo'lgani shu yerda ayni muddao.
 */
export const fileUrls = {
  preview: (id: string) => `${API_BASE}/v1/files/${id}/preview`,
  download: (id: string) => `${API_BASE}/employee/v1/files/${id}/download`,
};

/**
 * `Content-Range: bytes <start>-<end>/<total>` (206 javobi). Hajmi noma'lum
 * (`*`) yoki shakl boshqa bo'lsa — null.
 */
export function parseContentRange(
  header: unknown,
): { start: number; end: number; total: number } | null {
  if (typeof header !== 'string') return null;
  const m = /^bytes (\d+)-(\d+)\/(\d+)$/.exec(header.trim());
  if (!m) return null;
  const [start, end, total] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return start <= end && end < total ? { start, end, total } : null;
}
